import React, {useCallback} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import Select, {createFilter, Props as SelectProps, SelectComponentsConfig} from 'react-select';
import Async, {AsyncProps} from 'react-select/async';
import {dlReactSelect} from 'src/js/common/jsStyles/reactSelectStyles';
import {DlSelectSelectedValue} from 'src/js/common/types';
import {getValue} from 'src/js/common/utils/input_utils';
import TranslateUtils from 'src/js/common/utils/translate_utils';

import {DLSelectClearIndicator} from './DLSelectClearIndicator';
import {DLSelectMultiValueRemove} from './DLSelectMultiValueRemove';

const I18N = defineMessages({
  LOADING: {
    id: 'hierarchy.children.loading',
    defaultMessage: 'Loading ...',
    description: 'Dropdown label when options are loading',
  },
  NO_OPTIONS: {
    id: 'filter.dropDown.noOptions',
    defaultMessage: 'No matches found',
    description: 'Dropdown label when no options are available',
  },
  PLACEHOLDER: {
    id: 'filter.dropDown.placeholder',
    defaultMessage: 'Select...',
    description: 'placeholder in drop down input',
  },
});

// copied from react-select library, while these types are not properly exported there
export interface FilterOptionOption<Option> {
  readonly label: string;
  readonly value: string;
  readonly data: Option;
}

export interface DlSelectFilterConfig<Option = unknown> {
  readonly ignoreCase?: boolean;
  readonly ignoreAccents?: boolean;
  readonly stringify?: (option: FilterOptionOption<Option>) => string;
  readonly trim?: boolean;
  readonly matchFrom?: 'any' | 'start';
}

// default react-select filterOption config, it is not documented anywhere, so we show it here explicitly
const DEFAULT_FILTER_CONFIG: DlSelectFilterConfig = {
  ignoreCase: true,
  ignoreAccents: true,
  trim: true,
  matchFrom: 'any',
};

const DEFAULT_COMPONENTS: SelectComponentsConfig<any, boolean, any> = {
  ClearIndicator: DLSelectClearIndicator,
  MultiValueRemove: DLSelectMultiValueRemove,
};

const DEFAULT_VALUE_PROPERTY = 'value';
const DEFAULT_LABEL_PROPERTY = 'label';
const CLASS_NAME_PREFIX = 'dl-select';

interface OwnProps<Option = unknown> {
  // components?: Partial<SelectComponents<any, false, GroupBase<any>>> | undefined,
  filterConfig?: DlSelectFilterConfig;
  inputRef?: React.Ref<any>;
  isAsync?: boolean;
  isSearchable?: boolean;
  labelProperty?: string;
  noOptionsText?: string;
  loadingText?: string;
  options?: Option[];
  placeholderText?: string;
  renderInBody?: boolean;
  selectedValue?: DlSelectSelectedValue;
  valueProperty?: string;
}

export type DlSelectProps<Option = unknown> = OwnProps<Option> & AsyncProps<Option, any, any> & SelectProps<Option>;

export const DLSelect: React.FC<DlSelectProps> = (props) => {
  const {
    components,
    filterConfig = DEFAULT_FILTER_CONFIG,
    inputRef,
    isAsync = false,
    isSearchable = true,
    labelProperty = DEFAULT_LABEL_PROPERTY,
    loadingText = I18N.LOADING.id,
    noOptionsText = I18N.NO_OPTIONS.id,
    placeholderText = I18N.PLACEHOLDER.id,
    renderInBody = false,
    selectedValue,
    styles,
    valueProperty = DEFAULT_VALUE_PROPERTY,
    ...restProps
  } = props;

  const intl = useIntl();

  const loadingMessage = useCallback(() => TranslateUtils.translate(loadingText, intl), [intl, loadingText]);
  const noOptionsMessage = useCallback(() => TranslateUtils.translate(noOptionsText, intl), [intl, noOptionsText]);

  const filterOption = createFilter(filterConfig);

  const dynamicProps = {
    ...(renderInBody ? {menuPortalTarget: document.querySelector('body')} : {}),
    ...(isAsync
      ? {
          value: selectedValue,
          getOptionLabel: (option) => option[labelProperty],
          getOptionValue: (option) => option[valueProperty],
        }
      : {
          value: getValue(selectedValue, restProps.options, restProps.isMulti, valueProperty),
        }),
  };

  const SelectComponent = isAsync ? Async : Select;

  return (
    <SelectComponent
      classNamePrefix={CLASS_NAME_PREFIX}
      components={{...DEFAULT_COMPONENTS, ...components}}
      ref={inputRef}
      styles={{...dlReactSelect, ...styles}}
      filterOption={filterOption}
      isSearchable={isSearchable}
      loadingMessage={loadingMessage}
      noOptionsMessage={noOptionsMessage}
      placeholder={TranslateUtils.translate(placeholderText, intl)}
      dlHasShadowedBorder
      {...dynamicProps}
      {...restProps}
      value={getValue(selectedValue, restProps.options, restProps.isMulti, valueProperty)}
    />
  );
};

DLSelect.displayName = 'DLSelect';
